import * as React from 'react';
// eslint-disable-next-line no-restricted-imports
import type { CSS } from '@stitches/react';
import { withOnClickTracking, withOnLinkClickTracking } from 'auto-tracking';
import { TrackingProperties } from 'common-tracking';
import { ReactNode } from 'react';

import { styled, theme } from '../stitches.config';
import { shadowSize } from '../tokens/shadows';
import { Spinner } from './spinner';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
let NextLink: any;
try {
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  NextLink = require('./track-link').NextLink;
} catch {
  // NextLink not available, will use regular anchor tag
  NextLink = null;
}

export type ButtonProps = {
  /** What the button will render */
  children?: React.ReactNode;
  /** size of the button */
  size?: 'small' | 'medium' | 'large' | 'tiny';
  /** Pick the color of the button */
  color?:
    | 'primary'
    | 'secondary'
    | 'tertiary'
    | 'tertiaryReversed'
    | 'success'
    | 'danger'
    | 'outlined'
    | 'quaternary'
    | 'blueberry'
    | 'blueberryReversed'
    | 'royalBlue'
    | 'royalBlueReversed'
    | 'secondaryBlue'
    | 'yellow';
  /** Pick the variant of the Button */
  variant?: 'light' | 'solid' | 'bold' | 'extraBold' | 'link';
  dimmed?: boolean;
  /** Pick the type for the button */
  type?: 'button' | 'submit' | 'reset';
  /** disable the button */
  disabled?: boolean;
  /** give the button a with of 100% of it's parent */
  fullWidth?: boolean;
  /** callback function for the onClick trigger */
  onClick?: React.ButtonHTMLAttributes<HTMLButtonElement>['onClick'];
  onMouseEnter?: React.ButtonHTMLAttributes<HTMLButtonElement>['onMouseEnter'];
  onMouseLeave?: React.ButtonHTMLAttributes<HTMLButtonElement>['onMouseLeave'];
  /** To override the styling */
  css?: CSS;
  href?: string;
  target?: string;
  a?: string;
  tabIndex?: number;
  loading?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  id?: string;
  trackingName?: string;
  trackingProperties?: TrackingProperties;
  dataId?: string;
  ariaLabel?: string;
  disableTracking?: boolean;
  disableGa4Tracking?: boolean;
  title?: string;
};

const ContentContainer = styled('div', {
  columnGap: 8,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

function renderButtonLabel({
  children,
  leftIcon,
  rightIcon,
}: Pick<ButtonProps, 'leftIcon' | 'rightIcon' | 'children'>) {
  if (!leftIcon && !rightIcon) {
    return children;
  } else {
    return (
      <ContentContainer>
        {leftIcon ?? <></>}
        <div>{children ?? <></>}</div>
        {rightIcon ?? <></>}
      </ContentContainer>
    );
  }
}

/** This is the default simple button */
export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      target,
      children,
      size = 'medium',
      color = 'primary',
      variant = 'solid',
      disabled = false,
      fullWidth = false,
      type = 'button',
      loading = false,
      leftIcon,
      rightIcon,
      href,
      dataId,
      ariaLabel,
      ...props
    },
    ref,
  ) => {
    const label = renderButtonLabel({ leftIcon, rightIcon, children });

    const commonProps = {
      target,
      tabIndex: props.tabIndex,
      size: size || 'medium',
      color: color || 'primary',
      disabled: disabled || loading,
      fullWidth,
      type,
      variant: variant || 'solid',
      'data-test-id': dataId,
      'aria-label': ariaLabel,
      ...props,
      ref,
    };

    const buttonContent = loading ? (
      <>
        <Centered>
          <Spinner size={16} />
        </Centered>
        <Hidden>{label}</Hidden>
      </>
    ) : (
      label
    );

    if (href) {
      const { ref: _ref, ...linkProps } = commonProps;
      return (
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        <StyledLink href={href} {...(linkProps as any)}>
          {buttonContent}
        </StyledLink>
      );
    }
    return (
      <StyledButtonWithClick as="button" {...commonProps}>
        {buttonContent}
      </StyledButtonWithClick>
    );
  },
);
const Centered = styled('span', {
  position: 'absolute',
  left: 0,
  right: 0,
  bottom: 0,
  top: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

const Hidden = styled('span', {
  opacity: 0,
  display: 'flex',
});

const buttonStyles = {
  flexShrink: 0,
  position: 'relative',
  stroke: theme.colors.white,
  textDecoration: 'none',
  variants: {
    dimmed: {
      true: {},
      false: {},
    },
    fullWidth: {
      true: {
        display: 'inline-block',
        width: '100%',
      },
    },
    variant: {
      light: {
        fontWeight: 400,
      },
      solid: {
        fontWeight: 500,
      },
      bold: {
        fontWeight: 600,
      },
      extraBold: {
        fontWeight: 700,
      },
      link: {
        appearance: 'none',
        border: 'unset',
        backgroundColor: 'unset',
        boxShadow: 'none',
        padding: 0,
        margin: 0,
        color: '$$backgroundColor',
        '&:hover': {
          boxShadow: 'none',
        },
      },
    },
    thikness: {
      light: {
        fontWeight: 400,
      },
      bold: {
        fontWeight: 700,
      },
    },
    color: {
      primary: {
        $$foregroundColor: theme.colors.gray1,
        $$backgroundColor: theme.colors.blue9,
        $$borderColor: 'transparent',
        $$shadowColor: theme.colors.blueAlpha7,
        '&:active': {
          $$backgroundColor: theme.colors.blue10,
        },
        '&:disabled': {
          $$foregroundColor: theme.colors.blue3,
          $$backgroundColor: theme.colors.blue6,
          boxShadow: 'none',
        },
      },
      secondary: {
        $$foregroundColor: theme.colors.gray11,
        $$backgroundColor: theme.colors.gray1,
        $$borderColor: theme.colors.gray8,
        $$shadowColor: theme.colors.grayAlpha7,
        stroke: theme.colors.gray9,
        '&:active': {
          $$backgroundColor: theme.colors.gray2,
        },
        '&[data-state="active"], &[aria-selected="true"]': {
          $$backgroundColor: theme.colors.blue3,
          $$foregroundColor: theme.colors.blue12,
        },
        '&:disabled': {
          $$foregroundColor: theme.colors.gray6,
          boxShadow: 'none',
        },
      },
      tertiary: {
        $$foregroundColor: theme.colors.gray11,
        $$backgroundColor: 'rgba(255,255,255,0)',
        $$borderColor: 'unset',
        $$shadowColor: 'unset',
        stroke: theme.colors.gray11,
        '&:hover': {
          $$backgroundColor: theme.colors.gray3,
        },
        '&:active': {
          $$backgroundColor: theme.colors.gray4,
        },
        '&:disabled': {
          $$foregroundColor: theme.colors.gray7,
        },
        '&[data-state="active"], &[aria-selected="true"]': {
          $$backgroundColor: theme.colors.blue3,
          $$foregroundColor: theme.colors.blue12,
        },
      },
      tertiaryReversed: {
        $$foregroundColor: theme.colors.white,
        $$backgroundColor: 'rgba(255,255,255,0)',
        $$borderColor: 'unset',
        $$shadowColor: 'unset',
        stroke: theme.colors.white,
        '&:hover': {
          $$backgroundColor: 'rgba(255,255,255,0.1)',
        },
        '&:active': {
          $$backgroundColor: 'rgba(255,255,255,0.2)',
        },
        '&:disabled': {
          $$foregroundColor: 'rgba(255,255,255,0.7)',
        },
      },
      blueberry: {
        $$foregroundColor: theme.colors.blue12,
        $$backgroundColor: 'rgba(255,255,255,0)',
        $$borderColor: 'unset',
        $$shadowColor: 'unset',
        stroke: theme.colors.blue12,
        '&:hover': {
          $$backgroundColor: theme.colors.gray3,
        },
        '&:active': {
          $$backgroundColor: theme.colors.gray4,
        },
        '&:disabled': {
          $$foregroundColor: theme.colors.gray7,
        },
        '&[data-state="active"], &[aria-selected="true"]': {
          $$backgroundColor: theme.colors.blue3,
          $$foregroundColor: theme.colors.blue12,
        },
      },
      blueberryReversed: {
        $$foregroundColor: theme.colors.white,
        $$backgroundColor: theme.colors.blue12,
        $$borderColor: 'unset',
        $$shadowColor: 'unset',
        stroke: theme.colors.white,
        '&:hover': {
          $$backgroundColor: theme.colors.blue9,
        },
        '&:active': {
          $$backgroundColor: theme.colors.blue9,
        },
        '&:disabled': {
          $$foregroundColor: 'rgba(5,255,255,0.7)',
        },
      },
      royalBlue: {
        $$foregroundColor: 'rgb(27, 77, 192)',
        $$backgroundColor: 'rgba(255,255,255,0)',
        $$borderColor: 'unset',
        $$shadowColor: 'unset',
        stroke: 'rgb(27, 77, 192)',
        '&:hover': {
          $$backgroundColor: theme.colors.gray3,
        },
        '&:active': {
          $$backgroundColor: theme.colors.gray4,
        },
        '&:disabled': {
          $$foregroundColor: theme.colors.gray7,
        },
        '&[data-state="active"], &[aria-selected="true"]': {
          $$backgroundColor: theme.colors.blue3,
          $$foregroundColor: 'rgb(27, 77, 192)',
        },
      },
      royalBlueReversed: {
        $$foregroundColor: theme.colors.white,
        $$backgroundColor: 'rgb(27, 77, 192)',
        $$borderColor: 'unset',
        $$shadowColor: 'unset',
        stroke: theme.colors.white,
        '&:hover': {
          $$backgroundColor: theme.colors.blue12,
        },
        '&:active': {
          $$backgroundColor: theme.colors.blue12,
        },
        '&:disabled': {
          $$foregroundColor: 'rgba(5,255,255,0.7)',
        },
      },
      success: {
        $$foregroundColor: theme.colors.gray1,
        $$backgroundColor: theme.colors.green9,
        $$borderColor: 'transparent',
        $$shadowColor: theme.colors.greenAlpha7,
        '&:active': {
          $$backgroundColor: theme.colors.green10,
        },
        '&:disabled': {
          $$foregroundColor: theme.colors.green3,
          $$backgroundColor: theme.colors.green6,
        },
      },
      danger: {
        $$foregroundColor: theme.colors.gray1,
        $$backgroundColor: theme.colors.red9,
        $$borderColor: 'transparent',
        $$shadowColor: theme.colors.redAlpha7,
        '&:active': {
          $$backgroundColor: theme.colors.red10,
        },
        '&:disabled': {
          $$foregroundColor: theme.colors.red3,
          $$backgroundColor: theme.colors.red6,
        },
      },
      outlined: {
        $$foregroundColor: theme.colors.white,
        $$backgroundColor: 'transparent',
        $$borderColor: theme.colors.white,
      },
      quaternary: {
        $$backgroundColor: theme.colors.mustard,
        $$foregroundColor: theme.colors.white,
        $$borderColor: 'none',
      },
      secondaryBlue: {
        $$foregroundColor: theme.colors.blue10,
        $$backgroundColor: theme.colors.gray1,
        $$borderColor: theme.colors.blue10,
        $$shadowColor: theme.colors.grayAlpha7,
        '&:disabled': {
          $$foregroundColor: theme.colors.gray6,
          $$borderColor: theme.colors.gray6,
          boxShadow: 'none',
        },
      },
      yellow: {
        $$foregroundColor: theme.colors.blue12,
        $$backgroundColor: theme.colors.yellow7,
        $$borderColor: 'unset',
        $$shadowColor: 'unset',
        '&:hover': {
          $$backgroundColor: theme.colors.yellow6,
        },
      },
    },
    size: {
      tiny: {
        $$padding: '4px 8px',
        $$fontSize: theme.fontSizes['xs'],
        $$borderRadius: '6px',
      },
      small: {
        $$padding: '8px 12px',
        $$fontSize: theme.fontSizes['sm'],
        $$borderRadius: '6px',
      },
      medium: {
        $$padding: '12px 24px',
        $$fontSize: theme.fontSizes['base'],
        $$borderRadius: '8px',
      },
      large: {
        $$padding: '16px 32px',
        $$fontSize: theme.fontSizes['lg'],
        $$borderRadius: '10px',
      },
    },
  },

  compoundVariants: [
    {
      color: 'secondary',
      dimmed: true,
      css: {
        $$foregroundColor: theme.colors.gray8,
      },
    },
  ],

  color: '$$foregroundColor',
  backgroundColor: '$$backgroundColor',
  borderRadius: '$$borderRadius',
  fontFamily: 'inherit',
  fontSize: '$$fontSize',
  padding: '$$padding',
  textAlign: 'center',
  cursor: 'pointer',
  border: '1px solid $$borderColor',
  boxShadow: `${shadowSize[200]} $$shadowColor`,
  '&:active': {
    boxShadow: `${shadowSize[100]} $$shadowColor`,
  },
  '&:hover': {
    boxShadow: `${shadowSize[300]} $$shadowColor`,
  },
  '&:focus-visible': {
    boxShadow: `${shadowSize[300]} $$shadowColor`,
    outline: `2px solid ${theme.colors.yellow8}`,
    outlineOffset: '2px',
  },
  '&:disabled': {
    boxShadow: 'none',
    pointerEvents: 'none',
  },
};

const StyledButton = styled('button', buttonStyles);
const BaseLink = NextLink
  ? styled(NextLink, buttonStyles)
  : styled('a', buttonStyles);
const StyledLink = NextLink ? BaseLink : withOnLinkClickTracking(BaseLink);

const StyledButtonWithClick = withOnClickTracking(StyledButton);
